import backgroundImageUrl from 'assets/images/background-new-50368075.jpg';
import backgroundVideoUrl from 'assets/videos/background-new-50368075.mp4';

import { type FunctionComponent, useState } from 'react';
import { useNavigate } from 'react-router';

import { time } from '@dr.pogodin/react-utils';

import PageWithVideoBackground from 'components/PageWithVideoBackground';
import { ChatInterface, ChatPageHeader } from 'components/ai-app';

import { useExperience, useWellbeingAgent } from 'hooks';

import { getRealtimeAiClient } from 'services';
import { processInterview } from 'services/ai';

import {
  type InsightT,
  P_AIAPP_CHECK_IN,
  QuestionnaireKinds,
  addInsights,
  useGlobalState,
} from 'utils';

import './style.scss';

export const Onboarding: FunctionComponent = () => {
  const [audioSetup] = useGlobalState('ui.audioSetup');

  const [userId] = useGlobalState('user.id');
  if (!userId) throw Error('Not authenticated');

  const [, setOnboardingDone] = useGlobalState('user.onboardingDone');

  const experience = useExperience();
  const navigate = useNavigate();

  const [
    insights,
    setInsights,
  ] = useState<Record<string, InsightT[]>>({});

  // eslint-disable-next-line react/hook-use-state
  const [startedAt] = useState(Date.now);

  const [progress, setProgress] = useState(0);

  const { noAudioInputAfterMessageId, state, transcript } = useWellbeingAgent({
    experience: experience.data?.experience,
    mode: 'ONBOARDING',
    onSignal: (message) => {
      switch (message.kind) {
        case 'END':
          void (async () => {
            const realtime = await getRealtimeAiClient();
            const transcript2 = realtime.getCurrentTranscript();

            void processInterview({
              kind: QuestionnaireKinds.ONBOARDING_AI_INTERVIEW,
              sessionId: undefined,
              startedAt,
              transcript: transcript2,
              userDateTime: time(startedAt).toString(),
              userId,
            });

            setOnboardingDone(true);

            await navigate(P_AIAPP_CHECK_IN);
          })();
          break;
        case 'INSIGHT':
          void (async () => {
            const realtime = await getRealtimeAiClient();
            const transcript2 = realtime.getCurrentTranscript();
            setInsights(
              (old) => addInsights(old, transcript2, message.insights),
            );
          })();
          break;
        case 'PROGRESS_UPDATE':
          setProgress(message.progress);
          break;

        // NOTE: "INTERVIEW_BRANCH_CHANGE" is not supposed to be emitted in
        // the wellness check-out flow.
        case 'CHECK_IN_DONE':
        case 'DIRECTIVE_SESSION_REQUEST':
        case 'INTERVIEW_SEGMENT_CHANGE':
        default: throw Error('Unexpected case');
      }
    },
    sessionKind: experience.data?.sessionKind,
  });

  return audioSetup ? (
    // NOTE: This just mounts the AudioSetup page with the same background as
    // when it is first presented on the new user's landing to the Home page.
    <PageWithVideoBackground
      fallbackImageUrl={backgroundImageUrl}
      videoUrl={backgroundVideoUrl}
    >
      <div />
    </PageWithVideoBackground>
  ) : (
    <div styleName="container">
      <ChatPageHeader
        progress={progress}
        subTitle="Wellness Check-out"
        title="Soul Sanctuary"
      />
      <ChatInterface
        agentState={state}
        initialInputMode="mic"
        insights={insights}
        noAudioInputAfterMessageId={noAudioInputAfterMessageId}
        transcript={transcript}
      />
    </div>
  );
};
